import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserHelpGuides } from "../../hooks/useUserHelpGuides";
import Sidebar from "../Sidebar";
import { HELP_GUIDES, findArticle, getFirstArticle } from "./helpGuidesData";
import { BookIcon, Chevron, DownloadIcon, ExternalLinkIcon, PrintIcon } from "./HelpGuidesIcons";

const DEFAULT_ARTICLE_PARAM_KEY = "article";
const PDF_VIEWER_QUERY = "#toolbar=1&navpanes=0&view=FitH";

function buildDefaultOpenTopics() {
  return Object.fromEntries(HELP_GUIDES.map((topic) => [topic.id, true]));
}

function buildDefaultOpenSubtopics() {
  return Object.fromEntries(
    HELP_GUIDES.flatMap((topic) => (topic.subtopics || []).map((subtopic) => [subtopic.id, true]))
  );
}

function buildPdfViewerSrc(pdfPath) {
  return `${pdfPath}${PDF_VIEWER_QUERY}`;
}

export default function HelpGuides({ setAuth }) {
  const navigate = useNavigate();
  const { hasUserHelpGuidesAccess, isLoading: isGateLoading } = useUserHelpGuides();

  useEffect(() => {
    if (!isGateLoading && !hasUserHelpGuidesAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [isGateLoading, hasUserHelpGuidesAccess, navigate]);

  const [searchParams, setSearchParams] = useSearchParams();

  const articleIdFromUrl = searchParams.get(DEFAULT_ARTICLE_PARAM_KEY);
  const defaultSelection = useMemo(
    () => (articleIdFromUrl ? findArticle(articleIdFromUrl) : null) ?? getFirstArticle(),
    [articleIdFromUrl]
  );

  const [selectedArticleId, setSelectedArticleId] = useState(
    defaultSelection?.article?.id ?? null
  );

  // All topics/subtopics start expanded so users can scan all guides quickly.
  const [openTopics, setOpenTopics] = useState(buildDefaultOpenTopics);
  const [openSubtopics, setOpenSubtopics] = useState(buildDefaultOpenSubtopics);

  const selectedArticle = useMemo(
    () => (selectedArticleId ? findArticle(selectedArticleId) : null),
    [selectedArticleId]
  );

  const iframeRef = useRef(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");

  // Keep component state in sync when URL query param changes (e.g. back/forward nav).
  useEffect(() => {
    if (!defaultSelection?.article?.id) return;
    setSelectedArticleId((prev) =>
      prev === defaultSelection.article.id ? prev : defaultSelection.article.id
    );
  }, [defaultSelection]);

  // Persist selected article in URL for deep linking.
  useEffect(() => {
    if (!selectedArticleId) return;
    const current = searchParams.get(DEFAULT_ARTICLE_PARAM_KEY);
    if (current === selectedArticleId) return;
    const next = new URLSearchParams(searchParams);
    next.set(DEFAULT_ARTICLE_PARAM_KEY, selectedArticleId);
    setSearchParams(next, { replace: true });
  }, [selectedArticleId, searchParams, setSearchParams]);


  const toggleTopic = useCallback(
    (id) => setOpenTopics((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );
  const toggleSubtopic = useCallback(
    (id) => setOpenSubtopics((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  const handleSelectArticle = useCallback(
    (topicId, subtopicId, articleId) => {
      setOpenTopics((prev) => ({ ...prev, [topicId]: true }));
      setOpenSubtopics((prev) => ({ ...prev, [subtopicId]: true }));
      setSelectedArticleId(articleId);
      const match = findArticle(articleId);
      if (match) {
        setLiveAnnouncement(`Now viewing: ${match.article.title}`);
      }
    },
    []
  );

  const openInNewTab = useCallback(() => {
    if (!selectedArticle) return;
    window.open(selectedArticle.article.pdf, "_blank", "noopener,noreferrer");
  }, [selectedArticle]);

  const handlePrint = useCallback(() => {
    if (!selectedArticle) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      iframe.focus();
      iframe.contentWindow?.print();
    } catch {
      window.open(selectedArticle.article.pdf, "_blank", "noopener,noreferrer");
    }
  }, [selectedArticle]);

  if (isGateLoading) return null;
  if (!hasUserHelpGuidesAccess) return null;

  return (
    <>
      <Helmet>
        <title>User Guides – Knowtex</title>
      </Helmet>
      {/* Skip link — visible only on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-accent2 focus:text-white focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* Screen-reader live region for article selection announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      <Sidebar setAuth={setAuth} />
      <main id="main-content" tabIndex="-1" className="flex flex-col">
        <div className="flex flex-col transition-margin ml-sidebar-rail mr-6 pb-12 tablet:mt-3 desktop:ml-sidebar-open">
          {/* Page header */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50">
              <BookIcon />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-accent1">
                User Guides
              </h1>
              <p className="mt-0.5 text-sm text-secondary-text-grey">
                Browse topics and open a guide to read, print, or download.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col laptop:flex-row gap-6">
            {/* Topic tree */}
            <nav
              aria-label="User guides topics"
              className="w-full laptop:w-64 laptop:shrink-0 rounded-2xl border border-gray-200 bg-white shadow-sm self-start overflow-hidden"
            >
              {/* Nav header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                  Contents
                </span>
              </div>

              <ul className="flex flex-col p-2">
                {HELP_GUIDES.map((topic, topicIndex) => {
                  const topicOpen = Boolean(openTopics[topic.id]);
                  return (
                    <li
                      key={topic.id}
                      className={topicIndex > 0 ? "mt-1" : ""}
                    >
                      {/* Topic row */}
                      <button
                        type="button"
                        aria-expanded={topicOpen}
                        aria-controls={`topic-panel-${topic.id}`}
                        id={`topic-btn-${topic.id}`}
                        onClick={() => toggleTopic(topic.id)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-[13px] font-semibold transition-colors kx-focus ${
                          topicOpen
                            ? "text-accent2 bg-purple-50"
                            : "text-accent1 hover:bg-gray-50"
                        }`}
                      >
                        <span>{topic.title}</span>
                        <Chevron open={topicOpen} />
                      </button>

                      {topicOpen && (
                        <ul
                          id={`topic-panel-${topic.id}`}
                          aria-labelledby={`topic-btn-${topic.id}`}
                          className="mt-0.5 ml-3 border-l-2 border-purple-100 pl-2 flex flex-col gap-0.5 pb-1"
                        >
                          {topic.subtopics.map((subtopic) => {
                            const subOpen = Boolean(openSubtopics[subtopic.id]);
                            return (
                              <li key={subtopic.id}>
                                {/* Subtopic row */}
                                <button
                                  type="button"
                                  aria-expanded={subOpen}
                                  aria-controls={`subtopic-panel-${subtopic.id}`}
                                  id={`subtopic-btn-${subtopic.id}`}
                                  onClick={() => toggleSubtopic(subtopic.id)}
                                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left text-[12px] font-medium text-gray-500 hover:text-accent1 hover:bg-gray-50 transition-colors kx-focus"
                                >
                                  <span>{subtopic.title}</span>
                                  <Chevron open={subOpen} />
                                </button>

                                {subOpen && (
                                  <ul
                                    id={`subtopic-panel-${subtopic.id}`}
                                    aria-labelledby={`subtopic-btn-${subtopic.id}`}
                                    className="mt-0.5 ml-2 flex flex-col gap-0.5"
                                  >
                                    {subtopic.articles.map((article) => {
                                      const isActive =
                                        selectedArticleId === article.id;
                                      return (
                                        <li key={article.id}>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleSelectArticle(
                                                topic.id,
                                                subtopic.id,
                                                article.id
                                              )
                                            }
                                            aria-current={
                                              isActive ? "page" : undefined
                                            }
                                            className={`w-full px-3 py-2 rounded-md text-left text-[12px] leading-snug transition-colors kx-focus ${
                                              isActive
                                                ? "bg-purple-50 text-accent2 font-semibold border-l-2 border-accent2 rounded-l-none"
                                                : "text-gray-500 hover:text-accent1 hover:bg-gray-50"
                                            }`}
                                          >
                                            {article.title}
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* PDF viewer card */}
            <section
              aria-label="Article viewer"
              className="flex-1 rounded-2xl border-[1px] border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col min-h-[60vh] laptop:min-h-[75vh]"
            >
              {selectedArticle ? (
                <>
                  <header className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between border-b border-gray-100 px-5 py-4">
                    <div className="min-w-0">
                      <h2 className="text-accent1 text-base font-semibold tracking-tight truncate">
                        {selectedArticle.article.title}
                      </h2>
                      {selectedArticle.article.description && (
                        <p className="mt-0.5 text-secondary-text-grey text-xs">
                          {selectedArticle.article.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0" role="toolbar" aria-label="Article actions">
                      <button
                        type="button"
                        onClick={openInNewTab}
                        aria-label={`Open "${selectedArticle.article.title}" in new tab`}
                        title="Open in new tab"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-knowtex-grey hover:text-accent2 hover:bg-purple-50 transition-colors kx-focus"
                      >
                        <ExternalLinkIcon />
                      </button>
                      <button
                        type="button"
                        onClick={handlePrint}
                        aria-label={`Print "${selectedArticle.article.title}"`}
                        title="Print"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-knowtex-grey hover:text-accent2 hover:bg-purple-50 transition-colors kx-focus"
                      >
                        <PrintIcon />
                      </button>
                      <a
                        href={selectedArticle.article.pdf}
                        download
                        aria-label={`Download "${selectedArticle.article.title}" PDF`}
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-accent2 text-white text-sm font-semibold hover:bg-[#5B36AF] transition-colors kx-focus"
                      >
                        <DownloadIcon />
                        Download
                      </a>
                    </div>
                  </header>
                  <iframe
                    key={selectedArticle.article.id}
                    ref={iframeRef}
                    title={selectedArticle.article.title}
                    src={buildPdfViewerSrc(selectedArticle.article.pdf)}
                    className="flex-1 w-full bg-[#FBF9FF]"
                  />
                </>
              ) : (
                <div
                  role="status"
                  className="flex-1 flex items-center justify-center text-secondary-text-grey text-sm p-6"
                >
                  Select a guide on the left to view it here.
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
